import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type EmailPreferences = {
  reportsSummary: boolean;
  taskReminders: boolean;
  subscriptionAlerts: boolean;
  marketingEmails: boolean;
};

interface EmailPreferencesResponse {
  emailNotifications: EmailPreferences;
}

export const defaultEmailPreferences = {
  reportsSummary: true,
  taskReminders: true,
  subscriptionAlerts: true,
  marketingEmails: false,
};

export function useEmailPreferences() {
  const { toast } = useToast();
  const [localPreferences, setLocalPreferences] = useState<EmailPreferences | null>(null);

  // Fetch email preferences
  const { 
    data: preferences, 
    isLoading, 
    error 
  } = useQuery<EmailPreferencesResponse>({
    queryKey: ["/api/email-preferences"],
  });

  useEffect(() => {
    if (preferences?.emailNotifications && !localPreferences) {
      setLocalPreferences(preferences.emailNotifications);
    }
  }, [preferences, localPreferences]);

  // Update email preferences
  const { 
    mutate: updatePreferences,
    isPending: isUpdating
  } = useMutation({
    mutationFn: async (updatedPreferences: EmailPreferences) => {
      const res = await apiRequest(
        "PUT", 
        "/api/email-preferences", 
        { emailNotifications: updatedPreferences }
      );
      return await res.json();
    },
    onSuccess: (data) => {
      setLocalPreferences(data.emailNotifications);
      queryClient.setQueryData(["/api/email-preferences"], data);
      toast({
        title: "Preferences Updated",
        description: "Your email notification preferences have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to Update Preferences",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Update a single preference
  const updatePreference = (key: keyof EmailPreferences, value: boolean) => {
    if (localPreferences) {
      const updatedPreferences = {
        ...localPreferences,
        [key]: value,
      };
      setLocalPreferences(updatedPreferences);
    }
  };

  // Save all local preference changes
  const savePreferences = () => {
    if (localPreferences) {
      updatePreferences(localPreferences);
    }
  };

  // Schedule report email
  const scheduleReportEmail = async (businessId: number, reportType: string, frequency: string) => {
    try {
      const res = await apiRequest(
        "POST", 
        `/api/businesses/${businessId}/schedule-report`, 
        { reportType, frequency }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast({
          title: "Report Scheduled",
          description: data.message,
        });
        return true;
      } else {
        throw new Error(data.message || "Failed to schedule report");
      }
    } catch (error: any) {
      toast({
        title: "Failed to Schedule Report",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Cancel scheduled report email
  const cancelReportEmail = async (businessId: number, reportType: string) => {
    try {
      const res = await apiRequest(
        "DELETE", 
        `/api/businesses/${businessId}/schedule-report/${reportType}`
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast({
          title: "Report Canceled",
          description: data.message,
        });
        return true;
      } else {
        throw new Error(data.message || "Failed to cancel report");
      }
    } catch (error: any) {
      toast({
        title: "Failed to Cancel Report",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Schedule task reminders
  const scheduleTaskReminders = async (businessId: number, frequency: string) => {
    try {
      const res = await apiRequest(
        "POST", 
        `/api/businesses/${businessId}/schedule-task-reminders`, 
        { frequency }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast({
          title: "Task Reminders Scheduled",
          description: data.message,
        });
        return true;
      } else {
        throw new Error(data.message || "Failed to schedule task reminders");
      }
    } catch (error: any) {
      toast({
        title: "Failed to Schedule Task Reminders",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Cancel task reminders
  const cancelTaskReminders = async (businessId: number) => {
    try {
      const res = await apiRequest(
        "DELETE", 
        `/api/businesses/${businessId}/schedule-task-reminders`
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast({
          title: "Task Reminders Canceled",
          description: data.message,
        });
        return true;
      } else {
        throw new Error(data.message || "Failed to cancel task reminders");
      }
    } catch (error: any) {
      toast({
        title: "Failed to Cancel Task Reminders",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    preferences: localPreferences || preferences?.emailNotifications || defaultEmailPreferences,
    isLoading,
    error,
    isUpdating,
    updatePreference,
    savePreferences,
    scheduleReportEmail,
    cancelReportEmail,
    scheduleTaskReminders,
    cancelTaskReminders,
  };
}
